import clsx from "clsx";
import { FunctionComponent, useEffect, useRef, useState } from "react";

import CaretDownIcon from "@ui/components/icon/CaretDownIcon";
import { PMC_NETWORK_SITES } from "@ui/components/navigation/Footer/data/pmcNetworkSites";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";

import * as styles from "./NetworkSitesFooter.css";
import PmcLogo from "./PmcLogo";

const LIST_ID = "pmc-network-sites-list";

interface Props {
  className?: string;
  copyrightBrandName?: string;
}

const NetworkSitesFooter: FunctionComponent<Props> = ({
  className,
  copyrightBrandName,
}) => {
  const analytics = useAnalytics();
  const [open, setOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.inner}>
        <div className={styles.brandBlock}>
          <a
            href="https://pmc.com/"
            rel="nofollow"
            onClick={() =>
              analytics?.emit({
                event: "footer_click",
                footer_click: { name: "PMC Logo" },
              })
            }
          >
            <PmcLogo className={styles.logo} />
            <span className={styles.logoName}>Penske Media Corporation</span>
          </a>
          {copyrightBrandName && (
            <div className={styles.copyright}>
              <p className={styles.copyrightLine}>
                {copyrightBrandName} is a part of PMX Global, LLC, a subsidiary
                of Penske Media Corporation.
              </p>
              <p className={styles.copyrightLine}>
                © {year} VM Publishing, LLC. All rights reserved.
              </p>
            </div>
          )}
        </div>
        <nav
          ref={navRef}
          className={clsx(styles.nav, open && styles.navOpen)}
          aria-label="PMC network sites"
        >
          <h4>
            <button
              type="button"
              className={styles.navTrigger}
              aria-expanded={open}
              aria-controls={LIST_ID}
              onClick={() => setOpen((current) => !current)}
            >
              Our Sites
              <span
                className={clsx(
                  styles.navTriggerIcon,
                  open && styles.navTriggerIconOpen,
                )}
              >
                <CaretDownIcon width={10} height={6} />
              </span>
            </button>
          </h4>
          <ul id={LIST_ID} className={styles.navList} hidden={!open}>
            {PMC_NETWORK_SITES.map((site) => (
              <li key={site.url} className={styles.navItem}>
                <a
                  className={styles.navLink}
                  href={site.url}
                  rel="nofollow"
                  onClick={() =>
                    analytics?.emit({
                      event: "footer_click",
                      footer_click: { name: site.name },
                    })
                  }
                >
                  {site.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default NetworkSitesFooter;
